import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';
import { Criteria } from '@wb/core/interfaces/criteria';
import { DeleteCriteriaGQL } from '@wb/core/schema/mutation-definitions/criteria.graphql';
import {
  CountCriteriaGQL,
  CountQueryResponse,
  CriteriaWhere,
  CriteriasGQL,
  CriteriasQueryResponse,
} from '@wb/core/schema/query-definitions/criteria.graphql';
import { CriteriaItem, CriteriaService } from '@wb/core/services/criteria.service';
import { DisplayService } from '@wb/core/services/display.service';
import { RoutingService } from '@wb/core/services/routing.service';
import { CriteriaDialogComponent } from '@wb/shared/components/criteria-dialog/criteria-dialog.component';
import { Subject } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';

interface CriteriaRow {
  uid: string;
  items: CriteriaItem[];
}

interface Option {
  code: string;
  name: string;
}

@Component({
  selector: 'app-criteria-table',
  templateUrl: './criteria-table.component.html',
  styleUrls: ['./criteria-table.component.sass'],
  encapsulation: ViewEncapsulation.None,
})
export class CriteriaTableComponent implements OnDestroy, OnInit {
  displayedColumns: string[] = ['items', 'action'];

  private destroyed$: Subject<void> = new Subject<void>();

  private criteria$: Subject<Criteria[]> = new Subject<Criteria[]>();

  private count$: Subject<number> = new Subject();

  public dataSource: MatTableDataSource<CriteriaRow> = new MatTableDataSource([] as CriteriaRow[]);

  @ViewChild(MatPaginator) paginator: MatPaginator | undefined;

  public accountUid?: string;

  pageSize = 10;

  pageIndex = 0;

  input: string = '';

  length = 0;

  public isLoading = false;

  public removing = false;

  pageSizeOptions = [5, 10, 25];

  showFirstLastButtons = true;

  typeOptions: Option[] = [];

  selectedTypeIndex: number = 0;

  @Input() type?: string;

  @Input() parentUid?: string;

  @Input() parentType?: string;

  @Input() titleOverride?: string;

  @Input() headerAddButton?: boolean = false;

  @Input() pagination?: boolean;

  @Input() hideHeader?: boolean = true;

  @Input() hideActions?: boolean = false;

  @Input() disableLink?: boolean = false;

  @Input() updateList?: Subject<void>;

  @Output() isLoadingData: EventEmitter<boolean> = new EventEmitter(false);

  constructor(
    private readonly criteriasGQL: CriteriasGQL,
    private readonly countCriteriaGQL: CountCriteriaGQL,
    private readonly deleteCriteriaGQL: DeleteCriteriaGQL,
    private readonly routing: RoutingService,
    private readonly route: ActivatedRoute,
    private readonly dialog: MatDialog,
    private readonly criteriaService: CriteriaService,
    public readonly displayService: DisplayService,
  ) {}

  async ngOnInit() {
    this.accountUid = await this.routing.getSelectedAccountUid(this.route);

    this.isLoadingData.pipe(takeUntil(this.destroyed$)).subscribe((isLoading) => {
      this.isLoading = isLoading;
    });

    this.isLoadingData.next(true);

    this.loadList();
    this.loadCount();

    // If the criteria changes update the list
    this.criteriaService.criteriaAdded$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.loadList();
      this.loadCount();
    });

    // Update criteria
    this.criteria$.pipe(takeUntil(this.destroyed$)).subscribe((list) => {
      this.dataSource.data = list?.map((one) => ({
        uid: one.uid!,
        items: this.criteriaService.generateCriteriaItems(one),
      }));

      this.isLoadingData.next(false);
    });

    // Update criteria count
    this.count$.pipe(takeUntil(this.destroyed$)).subscribe((count) => {
      this.length = count;
    });

    // Force criteria updates
    this.updateList?.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.loadList();
      this.loadCount();
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  onTypeIndexChange(typeIndex: number) {
    if (typeIndex !== this.selectedTypeIndex) {
      this.selectedTypeIndex = typeIndex;

      this.length = 0;
      this.dataSource.data = [];
      this.pageIndex = 0;

      this.isLoadingData.next(true);

      this.loadList();
      this.loadCount();
    }
  }

  private loadList() {
    this.criteriasGQL
      .fetch(
        {
          where: this.generateWhere(),
          limit: this.pageSize,
          offset: this.pageIndex * this.pageSize,
          sortDirection: 'ASC',
        },
        { fetchPolicy: 'network-only' },
      )
      .pipe(
        map((result) => result.data),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: (response: CriteriasQueryResponse) => {
          this.criteria$.next(response.criterias.criteria);
        },
        error: (err: HttpErrorResponse) => {
          console.error(err);
        },
      });
  }

  private loadCount() {
    this.countCriteriaGQL
      .fetch({ where: this.generateWhere() }, { fetchPolicy: 'network-only' })
      .pipe(
        map((result) => result.data),
        takeUntil(this.destroyed$),
      )
      .subscribe((response: CountQueryResponse) => {
        this.count$.next(response.countCriteria.count);
      });
  }

  private generateWhere() {
    const where: CriteriaWhere = {
      accountUid: this.accountUid!,
      type: this.type,
    };
    Object.entries({
      ACCESSORIES: 'accessory',
      EXCLUSIONS: 'exclusion',
      FEES_TAXES: 'fee',
      FI: 'fi_add_on',
      PROTECTION: 'protection',
    }).find((value) => {
      if (this.type === value[1]) {
        where.type = value[0];
      }
    });

    if (this.parentUid) {
      if (this.type === 'accessory') {
        where.accessoryUid = this.parentUid;
      } else if (this.type === 'fee') {
        where.feeUid = this.parentUid;
      } else if (this.type === 'fi_add_on' || this.type === 'protection') {
        where.fiAddOnUid = this.parentUid;
      }
    }
    return where;
  }

  handlePageEvent(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;

    this.loadList();
  }

  onRemove(uid: string) {
    this.deleteCriteriaGQL
      .mutate({ uid })
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.loadList();
        this.loadCount();
      });
  }

  onAddCriteria() {
    let data: any = {
      type: this.type,
    };

    if (this.parentType) {
      data[this.parentType] = this.parentUid;
    }

    this.dialog
      .open(CriteriaDialogComponent, {
        autoFocus: false,
        role: 'alertdialog',
        width: '800px',
        data,
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((result) => {
        if (result) {
          this.loadList();
          this.loadCount();
        }
      });
  }

  onEditCriteria(uid: string) {
    if (!this.disableLink) {
      this.dialog
        .open(CriteriaDialogComponent, {
          autoFocus: false,
          role: 'alertdialog',
          width: '800px',
          data: {
            uid,
            type: this.type,
          },
        })
        .afterClosed()
        .pipe(take(1))
        .subscribe((result) => {
          if (result) {
            this.loadList();
          }
        });
    }
  }
}
